import { t } from "../../lib/i18n";
import React, { useState, useRef } from 'react';
import api from '../../api';
import { Link } from 'react-router-dom';

interface AnalysisResult {
  summary: string;
  risk_level: string;
  tips: string[];
}

interface LeaseInsightWidgetProps {
  slug: string;
}

const LeaseInsightWidget: React.FC<LeaseInsightWidgetProps> = ({ slug }) => {
  const [clauseText, setClauseText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ analysis?: AnalysisResult; limit_reached?: boolean; message?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size too large. Please upload a file smaller than 5MB.');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const toBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
  });

  const handleAnalyze = async () => {
    if (!clauseText.trim() && !file) {
      setError('Please provide a clause or upload a document.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let fileData = null;
      if (file) {
        fileData = await toBase64(file);
        // Check if base64 size is too large for a public endpoint (limiting to 2MB)
        if (fileData.length > 2 * 1024 * 1024) {
          setLoading(false);
          setError('Document is too complex for the public trial. Please try a smaller file or copy-paste the specific clause.');
          return;
        }
      }

      console.log(`[Public Insight] Sending payload. Text length: ${clauseText.length}, File data length: ${fileData?.length || 0}`);

      const response = await api.post('/blog/public-lease-insight', {
        clause_text: clauseText,
        file_data: fileData,
        file_name: file?.name,
        slug: slug
      });
      setResult(response.data);
    } catch (err: any) {
      console.error('Insight Error:', err);
      const msg = err.response?.data?.detail || 'The AI engine is currently busy. Please try again in 30 seconds.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="my-12 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border border-blue-100 shadow-sm overflow-hidden relative">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-20 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
      
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-600 rounded-2xl shadow-lg shadow-blue-200">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.364-7.364l-.707-.707m12.728 12.727l-.707-.707M6.364 17.636l-.707-.707M12 21v-1m0-5a5 5 0 110-10 5 5 0 010 10z" />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{t("leaseInsightWidget.k1")}</h3>
            <p className="text-sm text-gray-600">{t("leaseInsightWidget.k2")}</p>
          </div>
        </div>

        {!result ? (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">{t("leaseInsightWidget.k3")}</label>
              <textarea
                className="w-full p-4 rounded-2xl border-2 border-blue-100 focus:border-blue-500 focus:ring-0 transition-all text-base min-h-[120px] shadow-inner bg-white"
                placeholder={t("leaseInsightWidget.k4")}
                value={clauseText}
                onChange={(e) => setClauseText(e.target.value)}
              />
            </div>

            <div className="relative">
               <div className="flex items-center gap-4">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-dashed border-blue-200 rounded-xl text-blue-600 font-bold hover:border-blue-400 hover:bg-blue-50 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {file ? t("leaseInsightWidget.k5") : t("leaseInsightWidget.k6")}
                  </button>
                  {file && (
                    <div className="flex items-center gap-2 bg-blue-100 px-3 py-1 rounded-lg text-blue-800 text-sm font-medium animate-in fade-in slide-in-from-left-2">
                      <span className="truncate max-w-[150px]">{file.name}</span>
                      <button onClick={() => setFile(null)} className="hover:text-red-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
               </div>
               <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                className="hidden" 
                accept=".pdf,.doc,.docx,.txt"
               />
               <p className="text-[10px] text-gray-400 mt-2 ml-1 uppercase font-bold tracking-widest">{t("leaseInsightWidget.k7")}</p>
            </div>

            <button
              onClick={handleAnalyze}
              disabled={loading || (!clauseText.trim() && !file)}
              className="w-full px-8 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-black rounded-2xl transition-all shadow-xl shadow-blue-200 flex items-center justify-center gap-2 text-lg"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("leaseInsightWidget.k9")} </>
              ) : t("leaseInsightWidget.k8")}
            </button>
            {error && <p className="text-red-500 text-sm font-bold text-center animate-bounce">{error}</p>}
          </div>
        ) : result.limit_reached ? (
          <div className="bg-white p-8 rounded-2xl border border-red-100 text-center animate-in fade-in slide-in-from-bottom-4">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 0v2m0-2h2m-2 0H10m11 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">{t("leaseInsightWidget.k10")}</h4>
            <p className="text-gray-600 mb-6">{result.message}</p>
            <Link to="/login" className="inline-block px-8 py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg shadow-blue-200 hover:scale-105 transition-transform">
              {t("leaseInsightWidget.k11")} </Link>
            <button 
              onClick={() => setResult(null)}
              className="block w-full mt-6 text-xs font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600"
            >
              {t("leaseInsightWidget.k12")} </button>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-2xl border border-blue-50 shadow-sm animate-in zoom-in duration-300">
            <div className="flex justify-between items-start mb-4">
              <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border ${getRiskColor(result.analysis?.risk_level || '')}`}>
                {t("leaseInsightWidget.k13")} {result.analysis?.risk_level}
              </span>
              <button onClick={() => setResult(null)} className="text-gray-400 hover:text-gray-600 text-xs font-bold uppercase">{t("leaseInsightWidget.k14")}</button>
            </div>
            
            <div className="mb-6">
              <h4 className="text-[10px] font-black text-gray-400 uppercase mb-2 tracking-[0.2em]">{t("leaseInsightWidget.k15")}</h4>
              <p className="text-gray-800 font-medium leading-relaxed text-base">{result.analysis?.summary}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-[10px] font-black text-gray-400 uppercase mb-3 tracking-[0.2em]">{t("leaseInsightWidget.k16")}</h4>
              <ul className="space-y-3">
                {result.analysis?.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 p-4 rounded-xl border border-gray-100 shadow-sm">
                    <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Link to="/login" className="block w-full py-5 bg-black text-white text-center font-black rounded-2xl hover:bg-gray-900 transition-all hover:shadow-xl text-lg">
              {t("leaseInsightWidget.k17")} </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaseInsightWidget;
